import {
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
  effect,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { TourService } from '../../../services/tour.service';
import { WorkspaceContextService } from '../../../services/workspace-context.service';
import { buildTourSteps, isDelegatePanelLabel, tourMenuLabelsForPanel, tourPanelLabel } from '../../../services/tour-steps.util';

export interface SidebarMenuItem {
  label: string;
  icon?: string;
  route?: string;
  /** Optional query params (e.g. `{ tab: 'assetRequest' }`) applied with `routerLink`. */
  queryParams?: Record<string, string>;
  /**
   * Optional query params used only for active highlighting.
   * Values may be a single string or a list of accepted values.
   */
  activeMatchQueryParams?: Record<string, string | string[]>;
  href?: string;
  active?: boolean;
  children?: SidebarMenuItem[];
  safeIcon?: SafeHtml;
}

export interface SidebarCallToAction {
  title: string;
  description: string;
  linkText: string;
  linkRoute?: string;
  linkHref?: string;
  icon?: string;
  safeIcon?: SafeHtml;
}

export interface SidebarFooterItem {
  label: string;
  icon?: string;
  route?: string;
  href?: string;
  safeIcon?: SafeHtml;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  private _menuItems: SidebarMenuItem[] = [];

  @Input() set menuItems(items: SidebarMenuItem[]) {
    this._menuItems = (items || []).map((item) => ({
      ...item,
      safeIcon: item.icon ? this.sanitizer.bypassSecurityTrustHtml(item.icon) : undefined,
    }));
  }

  get menuItems(): SidebarMenuItem[] {
    return this._menuItems;
  }

  /** Panel name in the sidebar header (not `title` — that collides with the HTML global title tooltip). */
  @Input() headerLabel: string = 'Menu';
  
  private _callToAction?: SidebarCallToAction;
  @Input() set callToAction(value: SidebarCallToAction | undefined) {
    if (value) {
      this._callToAction = {
        ...value,
        safeIcon: value.icon ? this.sanitizer.bypassSecurityTrustHtml(value.icon) : undefined,
      };
    } else {
      this._callToAction = undefined;
    }
  }
  get callToAction(): SidebarCallToAction | undefined {
    return this._callToAction;
  }

  private _footerItems: SidebarFooterItem[] = [];
  @Input() set footerItems(items: SidebarFooterItem[] | undefined) {
    this._footerItems = (items || []).map((item) => ({
      ...item,
      safeIcon: item.icon ? this.sanitizer.bypassSecurityTrustHtml(item.icon) : undefined,
    }));
  }
  /**
   * Links shown under Panel Demo.
   * In a delegate session, Profile and Support are hidden so only Panel Demo remains.
   */
  get footerItems(): SidebarFooterItem[] {
    if (!this.workspaceContext.isDelegateSession()) {
      return this._footerItems;
    }
    return this._footerItems.filter(
      (item) => item.label !== 'Profile' && item.label !== 'Support',
    );
  }

  collapsed = false;
  offcanvasOpen = false;
  dropdownOpen = false;
  currentPageLabel = 'Dashboard';
  openDropdowns: Set<string> = new Set();

  readonly tour = inject(TourService);
  private readonly workspaceContext = inject(WorkspaceContextService);

  @ViewChildren('tourTarget') tourTargets!: QueryList<ElementRef<HTMLElement>>;

  // Play icon used by the Panel Demo footer button
  readonly demoIcon =
    '<polygon points="5 3 19 12 5 21 5 3" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>';
  demoSafeIcon: SafeHtml | undefined;

  constructor(
    private sanitizer: DomSanitizer,
    private router: Router,
  ) {
    this.demoSafeIcon = this.sanitizer.bypassSecurityTrustHtml(this.demoIcon);

    // When tour step changes: open offcanvas on mobile and scroll the target into view
    effect(() => {
      const active = this.tour.active();
      const mode = this.tour.mode();
      const step = this.tour.currentStep();
      if (!active || mode !== 'step' || !step) return;
      // Defer until DOM has tour-highlight class applied
      queueMicrotask(() => this.focusTourTarget(step.label));
    });
  }

  ngOnInit(): void {
    // Get initial page label and open relevant dropdowns
    this.updateCurrentPageLabel();
    this.updateOpenDropdowns();
    
    // Listen to route changes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateCurrentPageLabel();
        this.updateOpenDropdowns();
      });
  }

  /** True while any tour UI is open (welcome or step). */
  isTourActive(): boolean {
    return this.tour.active();
  }

  /** True while step mode is highlighting a sidebar item (not welcome). */
  isTourStepActive(): boolean {
    return this.tour.active() && this.tour.mode() === 'step';
  }

  isTourHighlighted(label: string): boolean {
    return this.isTourStepActive() && this.tour.currentStep()?.label === label;
  }

  startTour(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const panelLabel = tourPanelLabel(this.headerLabel || 'Consumer');
    const isDelegatePanel = isDelegatePanelLabel(panelLabel);
    const steps = buildTourSteps(
      tourMenuLabelsForPanel(
        this.menuItems.map((item) => item.label),
        isDelegatePanel,
      ),
      this.footerItems.map((item) => item.label),
    );
    this.tour.start(panelLabel, steps);
  }

  private focusTourTarget(label: string): void {
    const isMobileTablet = window.innerWidth < 992;
    if (isMobileTablet && !this.offcanvasOpen) {
      this.offcanvasOpen = true;
    }

    const run = (): void => {
      const targets = this.tourTargets?.toArray() ?? [];
      const match =
        targets.find((ref) => {
          const el = ref.nativeElement;
          return (
            el.getAttribute('data-tour-label') === label &&
            el.getClientRects().length > 0
          );
        }) ??
        targets.find(
          (ref) => ref.nativeElement.getAttribute('data-tour-label') === label,
        );

      const el = match?.nativeElement;
      if (!el) return;

      // Instant scroll so getBoundingClientRect matches the final spot before we animate
      el.scrollIntoView({ behavior: 'auto', block: 'nearest' });

      // Let the browser paint the dialog at its current (center / previous) spot,
      // then update the anchor so left+top transition together (no left-then-up).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.tour.setStepAnchor(el));
      });
    };

    setTimeout(run, isMobileTablet ? 180 : 0);
  }

  updateOpenDropdowns(): void {
    const currentRoute = this.router.url.split('?')[0].split('#')[0]; // Remove query params and fragments
    
    // Clear all dropdowns first
    this.openDropdowns.clear();
    
    // Check menu items and their children
    for (const item of this.menuItems) {
      // Check if current route matches any child route
      if (item.children && item.children.length > 0) {
        const activeChild = item.children.find(child => {
          if (!child.route) return false;
          const childRoute = child.route.split('?')[0].split('#')[0]; // Remove query params and fragments
          // Check if current route exactly matches or starts with the child route
          // This handles nested routes like /add-dataset/uploads matching /add-dataset
          return currentRoute === childRoute || 
                 currentRoute.startsWith(childRoute + '/');
        });
        if (activeChild) {
          // Keep dropdown open if we're on a child page
          this.openDropdowns.add(item.label);
        }
      }
    }
  }

  updateCurrentPageLabel(): void {
    const currentRoute = this.router.url.split('?')[0].split('#')[0]; // Remove query params and fragments
    
    // Check menu items and their children
    for (const item of this.menuItems) {
      // Check if current route matches item route
      if (item.route && this.isMenuItemActive(item)) {
        this.currentPageLabel = item.label;
        return;
      }
      
      // Check children - prefer query-param matches, then exact path, then prefix
      if (item.children && item.children.length > 0) {
        let activeChild = item.children.find((child) => this.isMenuItemActive(child));

        if (!activeChild) {
          activeChild = item.children.find((child) => {
            if (!child.route || child.queryParams) return false;
            const childRoute = child.route.split('?')[0].split('#')[0];
            return currentRoute === childRoute;
          });
        }

        if (!activeChild) {
          activeChild = item.children.find((child) => {
            if (!child.route || child.queryParams) return false;
            const childRoute = child.route.split('?')[0].split('#')[0];
            return currentRoute.startsWith(childRoute + '/');
          });
        }
        
        if (activeChild) {
          this.currentPageLabel = activeChild.label;
          return;
        }
      }
    }
    
    // Check footer items
    if (currentRoute.includes('/profile')) {
      this.currentPageLabel = 'Profile';
    } else if (currentRoute.includes('/support')) {
      this.currentPageLabel = 'Support';
    } else {
      this.currentPageLabel = 'Dashboard';
    }
  }

  /** Path (+ optional queryParams) match for highlighting menu items that share a route. */
  isMenuItemActive(item: SidebarMenuItem): boolean {
    if (!item.route) {
      return false;
    }

    const currentPath = this.router.url.split('?')[0].split('#')[0];
    const itemPath = item.route.split('?')[0].split('#')[0];
    const pathMatches =
      currentPath === itemPath || currentPath.startsWith(itemPath + '/');

    if (!pathMatches) {
      return false;
    }

    const matchParams = item.activeMatchQueryParams ?? item.queryParams;
    if (!matchParams || Object.keys(matchParams).length === 0) {
      return true;
    }

    const currentParams = this.router.parseUrl(this.router.url).queryParams;
    return Object.entries(matchParams).every(([key, value]) => {
      const current = currentParams[key];
      return Array.isArray(value) ? value.includes(current) : current === value;
    });
  }

  handleMenuClick(item: SidebarMenuItem, event?: Event): void {
    // Prevent event bubbling if provided (but allow default navigation)
    if (event) {
      event.stopPropagation();
    }
    
    // Update current page label
    this.currentPageLabel = item.label;
    
    // Ensure dropdown is open if this is a child item (do this before navigation)
    if (item.route) {
      // Find the parent item that contains this child
      for (const menuItem of this.menuItems) {
        if (menuItem.children && menuItem.children.some(child => child.route === item.route)) {
          // Open the dropdown before navigation to ensure it's visible
          if (!this.openDropdowns.has(menuItem.label)) {
            this.openDropdowns.add(menuItem.label);
          }
          break;
        }
      }
    }
    
    // Close offcanvas on mobile/tablet
    const isMobileTablet = window.innerWidth < 992;
    if (isMobileTablet) {
      this.closeOffcanvas();
    }
  }

  toggle(): void {
    this.collapsed = !this.collapsed;
  }

  toggleOffcanvas(): void {
    this.offcanvasOpen = !this.offcanvasOpen;
  }

  closeOffcanvas(): void {
    this.offcanvasOpen = false;
  }

  isOrganisationRouteActive(): boolean {
    const currentRoute = this.router.url;
    return currentRoute.includes('/join-organisation') || currentRoute.includes('/create-organisation');
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  toggleItemDropdown(itemLabel: string, event?: Event): void {
    // Prevent event from bubbling if provided
    if (event) {
      event.stopPropagation();
    }
    
    if (this.openDropdowns.has(itemLabel)) {
      // If clicking the same dropdown, close it
      this.openDropdowns.delete(itemLabel);
    } else {
      // Close all other dropdowns first, then open the clicked one
      this.openDropdowns.clear();
      this.openDropdowns.add(itemLabel);
    }
  }

  isDropdownOpen(itemLabel: string): boolean {
    return this.openDropdowns.has(itemLabel);
  }

  openOffcanvasFromDropdown(item: SidebarMenuItem): void {
    // Only open offcanvas on mobile/tablet (when dropdown is visible)
    // Check if we're on mobile/tablet by checking window width
    const isMobileTablet = window.innerWidth < 992; // lg breakpoint
    
    if (isMobileTablet) {
      // On mobile/tablet: open offcanvas sidebar
      this.dropdownOpen = false;
      this.offcanvasOpen = true;
    } else {
      // On desktop: just close dropdown, navigation will happen via routerLink
      this.dropdownOpen = false;
    }
  }
}
